/**
 * CMES — Module Gestion Interne (DG only)
 * Sprint 1: Clients + Factures + Paiements + Audit + Résumé stats
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Users, FileText, CreditCard, Banknote, ReceiptText, Wallet,
  BarChart3, Bot, ShieldCheck, Plus, Upload, Search, RefreshCw, Camera,
  X, Save, Trash2, Check, AlertCircle, Loader2, FileSpreadsheet, Filter,
  Download, Building2, TrendingUp, DollarSign
} from "lucide-react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import Layout from "../components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERNAL_ROLES = ["superadmin", "directeur_financier", "agent_recouvrement", "agent_comptable", "consultation_interne"];

const fmt = (n) => (Number(n) || 0).toLocaleString("fr-FR") + " FCFA";
const dateISO = () => new Date().toISOString().slice(0, 10);
const dateAdd = (days) => new Date(Date.now() + days * 86400000).toISOString().slice(0, 10);

const TABS = [
  { id: "recouvrements", label: "Recouvrements", icon: FileText, ready: true },
  { id: "remises", label: "Remises chèques", icon: Banknote, ready: true },
  { id: "especes", label: "Déclarations espèces", icon: Wallet, ready: true },
  { id: "depenses", label: "Dépenses", icon: ReceiptText, ready: true },
  { id: "tresorerie", label: "Trésorerie", icon: BarChart3, ready: true },
  { id: "alertes", label: "Alertes IA", icon: AlertCircle, ready: true },
  { id: "sentinelle", label: "Sentinelle IA", icon: Bot, ready: true },
  { id: "audit", label: "Journal audit", icon: ShieldCheck, ready: true },
];

const EXP_CATEGORIES = [
  { id: "salaires", label: "Salaires", color: "bg-blue-100 text-blue-700" },
  { id: "impots", label: "Impôts & Taxes", color: "bg-red-100 text-red-700" },
  { id: "loyer", label: "Loyer", color: "bg-amber-100 text-amber-700" },
  { id: "fournisseurs", label: "Fournisseurs", color: "bg-violet-100 text-violet-700" },
  { id: "utilities", label: "Eau/Élec/Internet", color: "bg-cyan-100 text-cyan-700" },
  { id: "carburant", label: "Carburant", color: "bg-orange-100 text-orange-700" },
  { id: "communication", label: "Communication", color: "bg-pink-100 text-pink-700" },
  { id: "bancaire", label: "Frais bancaires", color: "bg-slate-100 text-slate-700" },
  { id: "formation", label: "Formation", color: "bg-emerald-100 text-emerald-700" },
  { id: "voyages", label: "Voyages", color: "bg-teal-100 text-teal-700" },
  { id: "autres", label: "Autres", color: "bg-slate-100 text-slate-500" },
];

export default function InternalFinancePage() {
  const { user, token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [activeTab, setActiveTab] = useState("recouvrements");
  const [summary, setSummary] = useState(null);

  const authorized = INTERNAL_ROLES.includes(user?.role);

  const loadSummary = async () => {
    try {
      const res = await axios.get(`${API}/internal-finance/stats/summary`, { headers });
      setSummary(res.data);
    } catch {}
  };

  useEffect(() => {
    if (authorized) loadSummary();
  }, [authorized]);

  if (!authorized) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto mt-20 text-center p-8 bg-red-50 border border-red-200 rounded-2xl" data-testid="internal-forbidden">
          <ShieldCheck className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-800">Accès réservé</h2>
          <p className="text-red-600 mt-2">Ce module est privé et réservé à la direction interne de CMES.</p>
          <Link to="/dashboard" className="mt-6 inline-block text-blue-600 underline">← Retour</Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto p-4 md:p-6" data-testid="internal-finance-page">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <Link to="/superadmin" className="text-slate-500 hover:text-slate-700" data-testid="back-superadmin">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-black text-slate-900">Gestion Interne CMES</h1>
            <p className="text-sm text-slate-500">Espace privé — Recouvrements, Dépenses, Trésorerie & Sentinelle IA</p>
          </div>
          <Button variant="outline" size="sm" onClick={loadSummary} data-testid="refresh-summary">
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        </div>

        {/* KPI Strip */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KPICard icon={Users} label="Clients" value={summary.total_clients} color="cyan" testId="kpi-clients" />
            <KPICard icon={FileText} label="Factures émises" value={summary.total_invoices} color="violet" testId="kpi-invoices" />
            <KPICard icon={DollarSign} label="Total facturé" value={fmt(summary.total_billed_fcfa)} color="emerald" testId="kpi-billed" small />
            <KPICard icon={TrendingUp} label="Encours à recouvrer" value={fmt(summary.total_outstanding_fcfa)} color="amber" testId="kpi-outstanding" small />
          </div>
        )}

        {/* Tabs */}
        <div className="flex overflow-x-auto gap-1 mb-4 border-b border-slate-200 pb-1" data-testid="internal-tabs">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-semibold whitespace-nowrap transition-all ${
                  activeTab === t.id
                    ? "bg-white text-slate-900 border border-slate-200 border-b-white -mb-px"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
                {!t.ready && <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border">S{t.sprint}</span>}
              </button>
            );
          })}
        </div>

        {/* Content */}
        {activeTab === "recouvrements" && <RecouvrementsTab headers={headers} onRefresh={loadSummary} user={user} />}
        {activeTab === "remises" && <RemisesTab headers={headers} user={user} />}
        {activeTab === "especes" && <EspecesTab headers={headers} user={user} />}
        {activeTab === "depenses" && <DepensesTab headers={headers} user={user} />}
        {activeTab === "tresorerie" && <TresorerieTab headers={headers} />}
        {activeTab === "alertes" && <AlertesTab headers={headers} />}
        {activeTab === "sentinelle" && <SentinelleTab headers={headers} />}
        {activeTab === "audit" && <AuditTab headers={headers} />}
      </div>
    </Layout>
  );
}

function KPICard({ icon: Icon, label, value, color, testId, small }) {
  const colors = {
    cyan: "from-cyan-500 to-blue-500",
    violet: "from-violet-500 to-purple-500",
    emerald: "from-emerald-500 to-teal-500",
    amber: "from-amber-500 to-orange-500",
  };
  return (
    <Card className="p-4" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors[color]} flex items-center justify-center text-white`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">{label}</div>
          <div className={`${small ? "text-lg" : "text-2xl"} font-black text-slate-900 truncate`}>{value}</div>
        </div>
      </div>
    </Card>
  );
}

function PlaceholderTab({ tab }) {
  const Icon = tab.icon;
  return (
    <div className="text-center py-16 bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl">
      <Icon className="w-14 h-14 text-slate-400 mx-auto mb-3" />
      <h3 className="text-xl font-bold text-slate-700 mb-1">{tab.label}</h3>
      <p className="text-sm text-slate-500 mb-4">Sera livré au Sprint {tab.sprint}</p>
      <p className="text-xs text-slate-400 max-w-md mx-auto">
        Cette section est en cours de préparation. Le Sprint 1 couvre les Recouvrements et le Journal d'audit.
      </p>
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
 * RECOUVREMENTS TAB (Clients + Invoices + Payments)
 * ═════════════════════════════════════════════════════════════════════════ */
function RecouvrementsTab({ headers, onRefresh, user }) {
  const [subTab, setSubTab] = useState("invoices"); // invoices | clients
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(null); // invoice
  const [showImport, setShowImport] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const canWrite = ["superadmin", "directeur_financier", "agent_recouvrement"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [c, i] = await Promise.all([
        axios.get(`${API}/internal-finance/clients`, { headers, params: { q: searchQ || undefined } }),
        axios.get(`${API}/internal-finance/invoices`, { headers, params: { status: statusFilter || undefined } }),
      ]);
      setClients(c.data);
      setInvoices(i.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { load(); }, [statusFilter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex bg-slate-100 rounded-lg p-1">
          <button onClick={() => setSubTab("invoices")} data-testid="subtab-invoices"
            className={`px-3 py-1.5 rounded text-xs font-semibold ${subTab === "invoices" ? "bg-white shadow" : "text-slate-500"}`}>
            Factures ({invoices.length})
          </button>
          <button onClick={() => setSubTab("clients")} data-testid="subtab-clients"
            className={`px-3 py-1.5 rounded text-xs font-semibold ${subTab === "clients" ? "bg-white shadow" : "text-slate-500"}`}>
            Clients ({clients.length})
          </button>
        </div>

        <div className="flex-1 flex items-center gap-2">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
            <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Rechercher un client…" className="pl-8 h-9" data-testid="search-input" />
          </div>
          {subTab === "invoices" && (
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="h-9 border border-slate-200 rounded-md px-2 text-sm" data-testid="status-filter">
              <option value="">Tous statuts</option>
              <option value="emitted">Émise</option>
              <option value="partial">Partielle</option>
              <option value="paid">Payée</option>
              <option value="overdue">En retard</option>
              <option value="cancelled">Annulée</option>
            </select>
          )}
        </div>

        {canWrite && subTab === "clients" && (
          <>
            <Button size="sm" variant="outline" onClick={() => setShowImport(true)} data-testid="import-clients-btn">
              <Upload className="w-4 h-4 mr-1" /> Import Excel
            </Button>
            <Button size="sm" onClick={() => { setEditingClient(null); setShowClientForm(true); }} className="bg-cyan-600 hover:bg-cyan-700" data-testid="new-client-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouveau client
            </Button>
          </>
        )}
        {canWrite && subTab === "invoices" && (
          <Button size="sm" onClick={() => setShowInvoiceForm(true)} className="bg-violet-600 hover:bg-violet-700" data-testid="new-invoice-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle facture
          </Button>
        )}
      </div>

      {loading && <div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}

      {!loading && subTab === "invoices" && (
        <InvoicesTable
          invoices={invoices.filter(i => !searchQ || i.client_name?.toLowerCase().includes(searchQ.toLowerCase()) || i.number?.toLowerCase().includes(searchQ.toLowerCase()))}
          onPay={setShowPaymentForm}
          onRefresh={() => { load(); onRefresh(); }}
          headers={headers}
          canWrite={canWrite}
          canValidate={["superadmin", "directeur_financier"].includes(user?.role)}
        />
      )}
      {!loading && subTab === "clients" && (
        <ClientsTable clients={clients} onEdit={(c) => { setEditingClient(c); setShowClientForm(true); }} onRefresh={() => { load(); onRefresh(); }} headers={headers} canWrite={canWrite} />
      )}

      {showClientForm && (
        <ClientFormModal
          initial={editingClient}
          headers={headers}
          onClose={() => setShowClientForm(false)}
          onSaved={() => { setShowClientForm(false); load(); onRefresh(); }}
        />
      )}
      {showImport && (
        <ImportModal headers={headers} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); onRefresh(); }} />
      )}
      {showInvoiceForm && (
        <InvoiceFormModal
          headers={headers}
          clients={clients}
          onClose={() => setShowInvoiceForm(false)}
          onSaved={() => { setShowInvoiceForm(false); load(); onRefresh(); }}
        />
      )}
      {showPaymentForm && (
        <PaymentFormModal
          headers={headers}
          invoice={showPaymentForm}
          onClose={() => setShowPaymentForm(null)}
          onSaved={() => { setShowPaymentForm(null); load(); onRefresh(); }}
        />
      )}
    </div>
  );
}

function InvoicesTable({ invoices, onPay, onRefresh, headers, canWrite, canValidate }) {
  const [expanded, setExpanded] = useState(null);
  const [payments, setPayments] = useState({});

  const loadPayments = async (invId) => {
    try {
      const res = await axios.get(`${API}/internal-finance/payments`, { headers, params: { invoice_id: invId } });
      setPayments(prev => ({ ...prev, [invId]: res.data }));
    } catch {}
  };

  const toggle = (invId) => {
    if (expanded === invId) { setExpanded(null); }
    else { setExpanded(invId); if (!payments[invId]) loadPayments(invId); }
  };

  const validatePayment = async (pid) => {
    try {
      await axios.post(`${API}/internal-finance/payments/${pid}/validate`, {}, { headers });
      toast.success("Paiement validé");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (invoices.length === 0) {
    return <div className="text-center py-16 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">Aucune facture. Cliquez sur "Nouvelle facture".</div>;
  }
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="invoices-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left px-3 py-2">N°</th>
            <th className="text-left px-3 py-2">Client</th>
            <th className="text-left px-3 py-2">Description</th>
            <th className="text-right px-3 py-2">Montant</th>
            <th className="text-right px-3 py-2">Payé</th>
            <th className="text-right px-3 py-2">Solde</th>
            <th className="text-center px-3 py-2">Échéance</th>
            <th className="text-center px-3 py-2">Statut</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {invoices.map(inv => (
            <React.Fragment key={inv.id}>
              <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => toggle(inv.id)} data-testid={`invoice-row-${inv.id}`}>
                <td className="px-3 py-2 font-mono text-xs text-slate-700">{inv.number}</td>
                <td className="px-3 py-2 font-medium text-slate-800">{inv.client_name}</td>
                <td className="px-3 py-2 text-slate-600 truncate max-w-xs">{inv.description}</td>
                <td className="px-3 py-2 text-right font-semibold">{fmt(inv.amount)}</td>
                <td className="px-3 py-2 text-right text-emerald-700">{fmt(inv.amount_paid)}</td>
                <td className="px-3 py-2 text-right text-amber-700 font-semibold">{fmt(inv.balance)}</td>
                <td className="px-3 py-2 text-center text-xs text-slate-500">{inv.due_date}</td>
                <td className="px-3 py-2 text-center"><InvoiceStatus s={inv.status} /></td>
                <td className="px-3 py-2 text-right space-x-1">
                  {canWrite && inv.status !== "paid" && inv.status !== "cancelled" && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); onPay(inv); }} data-testid={`pay-btn-${inv.id}`}>
                      <CreditCard className="w-3 h-3 mr-1" /> Paiement
                    </Button>
                  )}
                </td>
              </tr>
              {expanded === inv.id && (
                <tr>
                  <td colSpan="9" className="bg-slate-50 p-3">
                    <div className="text-xs font-semibold text-slate-600 mb-2">Paiements enregistrés :</div>
                    {(payments[inv.id] || []).length === 0 && <div className="text-xs text-slate-400 italic">Aucun paiement</div>}
                    {(payments[inv.id] || []).map(p => (
                      <div key={p.id} className="flex items-center gap-3 py-1.5 border-b border-slate-200 last:border-0" data-testid={`payment-row-${p.id}`}>
                        <ModeIcon mode={p.mode} />
                        <span className="text-sm font-semibold w-28">{fmt(p.amount)}</span>
                        <span className="text-xs text-slate-500 w-20">{p.payment_date}</span>
                        <span className="text-xs text-slate-500 flex-1 truncate">{p.reference || p.comment}</span>
                        <span className="text-[10px] text-slate-400">par {p.created_by_name}</span>
                        {p.photo && (
                          <a href={p.photo} target="_blank" rel="noreferrer" className="text-xs text-cyan-600 underline">📷 Justif</a>
                        )}
                        {p.status === "pending_validation" && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-700">À valider</span>
                        )}
                        {p.status === "validated" && (
                          <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700">✓ Validé</span>
                        )}
                        {canValidate && p.status === "pending_validation" && (
                          <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => validatePayment(p.id)} data-testid={`validate-payment-${p.id}`}>Valider</Button>
                        )}
                      </div>
                    ))}
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClientsTable({ clients, onEdit, onRefresh, headers, canWrite }) {
  if (clients.length === 0) {
    return <div className="text-center py-16 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">Aucun client. Ajoutez-en ou importez un fichier Excel.</div>;
  }
  const del = async (id, name) => {
    if (!window.confirm(`Supprimer le client "${name}" ?`)) return;
    try {
      await axios.delete(`${API}/internal-finance/clients/${id}`, { headers });
      toast.success("Client supprimé");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid="clients-table">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left px-3 py-2">Nom</th>
            <th className="text-left px-3 py-2">Type</th>
            <th className="text-left px-3 py-2">Contact</th>
            <th className="text-left px-3 py-2">Téléphone</th>
            <th className="text-right px-3 py-2">Facturé</th>
            <th className="text-right px-3 py-2">Payé</th>
            <th className="text-right px-3 py-2">Solde dû</th>
            <th className="text-right px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {clients.map(c => (
            <tr key={c.id} className="hover:bg-slate-50" data-testid={`client-row-${c.id}`}>
              <td className="px-3 py-2 font-semibold text-slate-800">{c.name}</td>
              <td className="px-3 py-2 text-xs">
                <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 uppercase">{c.type}</span>
              </td>
              <td className="px-3 py-2 text-slate-600">{c.contact_name || "—"}</td>
              <td className="px-3 py-2 text-slate-600">{c.phone || "—"}</td>
              <td className="px-3 py-2 text-right text-slate-700">{fmt(c.total_billed)}</td>
              <td className="px-3 py-2 text-right text-emerald-700">{fmt(c.total_paid)}</td>
              <td className="px-3 py-2 text-right text-amber-700 font-semibold">{fmt(c.balance_due)}</td>
              <td className="px-3 py-2 text-right space-x-1">
                {canWrite && (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onEdit(c)} data-testid={`edit-client-${c.id}`}>Modifier</Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600" onClick={() => del(c.id, c.name)} data-testid={`delete-client-${c.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function InvoiceStatus({ s }) {
  const cfg = {
    emitted: { l: "Émise", c: "bg-blue-100 text-blue-700" },
    partial: { l: "Partielle", c: "bg-amber-100 text-amber-700" },
    paid: { l: "Payée", c: "bg-emerald-100 text-emerald-700" },
    overdue: { l: "En retard", c: "bg-red-100 text-red-700" },
    cancelled: { l: "Annulée", c: "bg-slate-100 text-slate-500 line-through" },
  }[s] || { l: s, c: "bg-slate-100" };
  return <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded ${cfg.c}`}>{cfg.l}</span>;
}

function ModeIcon({ mode }) {
  const cfg = {
    cash: { i: Banknote, c: "text-emerald-600", l: "Espèces" },
    check: { i: FileText, c: "text-blue-600", l: "Chèque" },
    transfer: { i: CreditCard, c: "text-violet-600", l: "Virement" },
  }[mode] || { i: FileText, c: "text-slate-500", l: mode };
  const Icon = cfg.i;
  return <span className={`inline-flex items-center gap-1 text-xs ${cfg.c}`}><Icon className="w-3.5 h-3.5" /> {cfg.l}</span>;
}

/* ─── Modals ────────────────────────────────────────────────────────────── */
function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between">
          <h3 className="font-bold text-slate-800">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

function ClientFormModal({ initial, headers, onClose, onSaved }) {
  const [f, setF] = useState(initial || { name: "", type: "entreprise", contact_name: "", phone: "", email: "", address: "", ville: "", nif: "", credit_limit: 0, notes: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.name?.trim()) return toast.error("Nom obligatoire");
    setSaving(true);
    try {
      if (initial?.id) {
        await axios.put(`${API}/internal-finance/clients/${initial.id}`, f, { headers });
        toast.success("Client mis à jour");
      } else {
        await axios.post(`${API}/internal-finance/clients`, f, { headers });
        toast.success("Client créé");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const F = (k, label, type = "text") => (
    <div>
      <label className="text-xs font-semibold text-slate-500 block mb-1">{label}</label>
      <Input type={type} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: type === "number" ? Number(e.target.value) : e.target.value })} data-testid={`client-field-${k}`} />
    </div>
  );
  return (
    <Modal title={initial ? "Modifier le client" : "Nouveau client"} onClose={onClose} wide>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {F("name", "Nom / Raison sociale *")}
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Type</label>
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="client-field-type">
            <option value="entreprise">Entreprise</option>
            <option value="pharmacie">Pharmacie</option>
            <option value="administration">Administration</option>
            <option value="particulier">Particulier</option>
          </select>
        </div>
        {F("contact_name", "Nom du contact")}
        {F("phone", "Téléphone")}
        {F("email", "Email")}
        {F("ville", "Ville")}
        {F("address", "Adresse")}
        {F("nif", "NIF / Immat. fiscale")}
        {F("credit_limit", "Plafond crédit (FCFA)", "number")}
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-500 block mb-1">Notes</label>
        <textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3} className="w-full border rounded-md p-2 text-sm" data-testid="client-field-notes" />
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
        <Button onClick={submit} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-client-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer
        </Button>
      </div>
    </Modal>
  );
}

function ImportModal({ headers, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const upload = async () => {
    if (!file) return toast.error("Choisissez un fichier");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/internal-finance/clients/import`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success(`${res.data.inserted} clients importés (${res.data.skipped} ignorés)`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur import"); }
    finally { setUploading(false); }
  };
  return (
    <Modal title="Import Excel / CSV" onClose={onClose}>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800 mb-3">
        <div className="font-semibold mb-1">Colonnes attendues (première ligne = en-têtes) :</div>
        <div className="font-mono">name*, type, contact_name, phone, email, address, ville, nif, credit_limit</div>
        <div className="mt-1 italic">Formats acceptés : .xlsx, .xls, .csv — doublons de nom automatiquement ignorés.</div>
      </div>
      <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0])} className="w-full text-sm" data-testid="import-file-input" />
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onClose} disabled={uploading}>Annuler</Button>
        <Button onClick={upload} disabled={uploading || !file} className="bg-emerald-600 hover:bg-emerald-700" data-testid="import-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}Importer
        </Button>
      </div>
    </Modal>
  );
}

function InvoiceFormModal({ headers, clients, onClose, onSaved }) {
  const [f, setF] = useState({
    client_id: clients[0]?.id || "", description: "", amount: 0,
    emission_date: dateISO(), due_date: dateAdd(30)
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.client_id) return toast.error("Sélectionnez un client");
    if (!f.description) return toast.error("Description obligatoire");
    if (f.amount <= 0) return toast.error("Montant invalide");
    setSaving(true);
    try {
      await axios.post(`${API}/internal-finance/invoices`, f, { headers });
      toast.success("Facture émise");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Nouvelle facture" onClose={onClose}>
      <div className="space-y-3">
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Client *</label>
          <select value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="invoice-client">
            <option value="">— Choisir —</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Description / Prestation *</label>
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} className="w-full border rounded-md p-2 text-sm" data-testid="invoice-desc" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Montant (FCFA) *</label>
            <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: Number(e.target.value) })} data-testid="invoice-amount" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Émission</label>
            <Input type="date" value={f.emission_date} onChange={(e) => setF({ ...f, emission_date: e.target.value })} data-testid="invoice-emission" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500 block mb-1">Échéance</label>
            <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="invoice-due" />
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
        <Button onClick={submit} disabled={saving} className="bg-violet-600 hover:bg-violet-700" data-testid="save-invoice-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Émettre
        </Button>
      </div>
    </Modal>
  );
}

function PaymentFormModal({ headers, invoice, onClose, onSaved }) {
  const [f, setF] = useState({
    amount: invoice.balance, mode: "cash", payment_date: dateISO(),
    reference: "", bank: "", comment: "",
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const camRef = useRef(null);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const runCheckOCR = async () => {
    if (!photo || f.mode !== "check") return;
    setOcrLoading(true);
    try {
      const fd = new FormData();
      fd.append("photo", photo);
      const res = await axios.post(`${API}/internal-finance/ocr/check`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      const d = res.data || {};
      setF(prev => ({
        ...prev,
        amount: d.amount || prev.amount,
        reference: d.check_number || prev.reference,
        bank: d.bank || prev.bank,
        payment_date: d.date || prev.payment_date,
        comment: prev.comment || (d.amount_words ? `Montant en lettres: ${d.amount_words}` : prev.comment),
      }));
      toast.success(`OCR: ${d.amount ? d.amount + " FCFA" : "champs pré-remplis"}${d.confidence ? ` (${d.confidence}%)` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "OCR échoué");
    } finally {
      setOcrLoading(false);
    }
  };

  const submit = async () => {
    if (f.amount <= 0) return toast.error("Montant invalide");
    if (f.amount > invoice.balance + 0.01) return toast.error("Montant supérieur au solde restant");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("invoice_id", invoice.id);
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (photo) fd.append("photo", photo);
      await axios.post(`${API}/internal-finance/payments`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Paiement enregistré");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={`Paiement — ${invoice.number} (${invoice.client_name})`} onClose={onClose} wide>
      <div className="bg-slate-50 rounded-lg p-3 mb-4 text-sm">
        <div className="flex justify-between"><span>Montant facture :</span><span className="font-semibold">{fmt(invoice.amount)}</span></div>
        <div className="flex justify-between text-emerald-700"><span>Déjà payé :</span><span className="font-semibold">{fmt(invoice.amount_paid)}</span></div>
        <div className="flex justify-between text-amber-700 font-bold border-t pt-1 mt-1"><span>Solde restant :</span><span>{fmt(invoice.balance)}</span></div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Montant reçu (FCFA) *</label>
          <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: Number(e.target.value) })} data-testid="pay-amount" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Mode *</label>
          <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="pay-mode">
            <option value="cash">Espèces</option>
            <option value="check">Chèque</option>
            <option value="transfer">Virement</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Date paiement</label>
          <Input type="date" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} data-testid="pay-date" />
        </div>
        {f.mode !== "cash" && (
          <>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">
                {f.mode === "check" ? "N° chèque" : "Référence virement"}
              </label>
              <Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="pay-reference" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">Banque</label>
              <Input value={f.bank} onChange={(e) => setF({ ...f, bank: e.target.value })} data-testid="pay-bank" />
            </div>
          </>
        )}
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-500 block mb-1">Commentaire</label>
        <textarea value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} rows={2} className="w-full border rounded-md p-2 text-sm" data-testid="pay-comment" />
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-500 block mb-1">
          Photo justificatif {f.mode === "check" && <span className="text-red-500">(chèque recommandé)</span>}
        </label>
        <div className="flex gap-2 flex-wrap">
          <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" data-testid="pay-photo-input" />
          <Button variant="outline" onClick={() => camRef.current?.click()} className="gap-2" data-testid="pay-photo-btn">
            <Camera className="w-4 h-4" /> {photo ? "Changer" : "Prendre / Choisir"}
          </Button>
          {photo && f.mode === "check" && (
            <Button variant="outline" onClick={runCheckOCR} disabled={ocrLoading} className="gap-2 border-fuchsia-300 text-fuchsia-700 hover:bg-fuchsia-50" data-testid="pay-ocr-btn">
              {ocrLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
              Lire le chèque (IA)
            </Button>
          )}
          {photo && <span className="text-xs text-slate-500 self-center truncate">{photo.name}</span>}
        </div>
        {photoPreview && (
          <img src={photoPreview} alt="preview" className="mt-2 max-h-48 rounded-lg border" />
        )}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
        <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-payment-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}Enregistrer paiement
        </Button>
      </div>
    </Modal>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
 * REMISES DE CHÈQUES TAB (Sprint 2)
 * ═════════════════════════════════════════════════════════════════════════ */
function RemisesTab({ headers, user }) {
  const [pending, setPending] = useState([]);
  const [remittances, setRemittances] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const canWrite = ["superadmin", "directeur_financier", "agent_recouvrement"].includes(user?.role);
  const canValidate = ["superadmin", "directeur_financier"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/internal-finance/checks/pending`, { headers }),
        axios.get(`${API}/internal-finance/remittances`, { headers }),
      ]);
      setPending(p.data);
      setRemittances(r.data);
    } catch (e) { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleSel = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const selectedTotal = pending.filter(c => selected.has(c.id)).reduce((s, c) => s + c.amount, 0);

  const doCredit = async (id) => {
    const date = window.prompt("Date de crédit (YYYY-MM-DD):", dateISO());
    if (!date) return;
    try {
      const fd = new FormData();
      fd.append("credit_date", date);
      await axios.post(`${API}/internal-finance/remittances/${id}/credit`, fd, { headers });
      toast.success("Remise créditée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  const doReject = async (id) => {
    const reason = window.prompt("Motif du rejet :", "Chèque impayé");
    if (reason === null) return;
    try {
      const fd = new FormData();
      fd.append("reason", reason);
      await axios.post(`${API}/internal-finance/remittances/${id}/reject`, fd, { headers });
      toast.success("Remise rejetée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-6" data-testid="remises-tab">
      {/* Pending checks */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-bold text-slate-800">Chèques en attente de remise ({pending.length})</h3>
            <p className="text-xs text-slate-500">Sélectionnez les chèques à déposer en banque</p>
          </div>
          {selected.size > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-emerald-700">
                {selected.size} sélectionné{selected.size > 1 ? "s" : ""} — Total : {fmt(selectedTotal)}
              </span>
              {canWrite && (
                <Button size="sm" onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-remittance-btn">
                  <Plus className="w-4 h-4 mr-1" /> Créer bordereau
                </Button>
              )}
            </div>
          )}
        </div>
        {loading && <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}
        {!loading && pending.length === 0 && <div className="text-center py-6 text-sm text-slate-400">Aucun chèque en attente</div>}
        {!loading && pending.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b">
              <tr>
                <th className="p-2 w-8"></th>
                <th className="text-left p-2">Facture</th>
                <th className="text-left p-2">Client</th>
                <th className="text-left p-2">N° chèque</th>
                <th className="text-left p-2">Banque</th>
                <th className="text-right p-2">Montant</th>
                <th className="text-center p-2">Date</th>
              </tr>
            </thead>
            <tbody>
              {pending.map(c => (
                <tr key={c.id} className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${selected.has(c.id) ? "bg-emerald-50" : ""}`}
                  onClick={() => toggleSel(c.id)} data-testid={`check-row-${c.id}`}>
                  <td className="p-2"><input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleSel(c.id)} data-testid={`check-select-${c.id}`} /></td>
                  <td className="p-2 font-mono text-xs">{c.invoice_number}</td>
                  <td className="p-2 font-medium">{c.client_name}</td>
                  <td className="p-2 text-slate-600">{c.reference || "—"}</td>
                  <td className="p-2 text-slate-600">{c.bank || "—"}</td>
                  <td className="p-2 text-right font-semibold">{fmt(c.amount)}</td>
                  <td className="p-2 text-center text-xs text-slate-500">{c.payment_date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Remittances history */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h3 className="font-bold text-slate-800 mb-3">Historique des bordereaux ({remittances.length})</h3>
        {remittances.length === 0 && <div className="text-center py-6 text-sm text-slate-400">Aucun bordereau</div>}
        {remittances.length > 0 && (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b">
              <tr>
                <th className="text-left p-2">N°</th>
                <th className="text-left p-2">Banque</th>
                <th className="text-center p-2">Chèques</th>
                <th className="text-right p-2">Total</th>
                <th className="text-center p-2">Date remise</th>
                <th className="text-center p-2">Statut</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {remittances.map(r => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`remittance-row-${r.id}`}>
                  <td className="p-2 font-mono text-xs">{r.number}</td>
                  <td className="p-2 font-medium">{r.bank}</td>
                  <td className="p-2 text-center">{r.checks_count}</td>
                  <td className="p-2 text-right font-semibold text-emerald-700">{fmt(r.total_amount)}</td>
                  <td className="p-2 text-center text-xs">{r.remittance_date}</td>
                  <td className="p-2 text-center"><RemStatus s={r.status} /></td>
                  <td className="p-2 text-right space-x-1">
                    {r.status === "deposited" && canValidate && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => doCredit(r.id)} data-testid={`credit-btn-${r.id}`}>
                          <Check className="w-3 h-3 mr-1" /> Créditer
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600" onClick={() => doReject(r.id)} data-testid={`reject-btn-${r.id}`}>
                          <X className="w-3 h-3 mr-1" /> Rejeter
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showForm && (
        <RemittanceFormModal
          headers={headers}
          selectedChecks={pending.filter(c => selected.has(c.id))}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); setSelected(new Set()); load(); }}
        />
      )}
    </div>
  );
}

function RemStatus({ s }) {
  const cfg = {
    deposited: { l: "Déposée", c: "bg-amber-100 text-amber-700" },
    credited: { l: "✓ Créditée", c: "bg-emerald-100 text-emerald-700" },
    rejected: { l: "✗ Rejetée", c: "bg-red-100 text-red-700" },
  }[s] || { l: s, c: "bg-slate-100" };
  return <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded ${cfg.c}`}>{cfg.l}</span>;
}

function RemittanceFormModal({ headers, selectedChecks, onClose, onSaved }) {
  const total = selectedChecks.reduce((s, c) => s + c.amount, 0);
  const [f, setF] = useState({ bank: selectedChecks[0]?.bank || "", account: "", remittance_date: dateISO() });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.bank) return toast.error("Banque obligatoire");
    setSaving(true);
    try {
      await axios.post(`${API}/internal-finance/remittances`, {
        ...f, payment_ids: selectedChecks.map(c => c.id)
      }, { headers });
      toast.success(`Bordereau créé : ${fmt(total)}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Créer un bordereau de remise" onClose={onClose} wide>
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-4">
        <div className="text-xs font-semibold text-emerald-800 mb-1">{selectedChecks.length} chèque(s) sélectionné(s)</div>
        <div className="text-2xl font-black text-emerald-700">{fmt(total)}</div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Banque de dépôt *</label>
          <Input value={f.bank} onChange={(e) => setF({ ...f, bank: e.target.value })} placeholder="NSIA, SGCI, BOA…" data-testid="rem-bank" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Compte</label>
          <Input value={f.account} onChange={(e) => setF({ ...f, account: e.target.value })} placeholder="N° compte" data-testid="rem-account" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Date de remise</label>
          <Input type="date" value={f.remittance_date} onChange={(e) => setF({ ...f, remittance_date: e.target.value })} data-testid="rem-date" />
        </div>
      </div>
      <div className="text-xs font-semibold text-slate-600 mb-2">Détail des chèques :</div>
      <div className="max-h-48 overflow-y-auto text-xs bg-slate-50 rounded-lg p-2 border">
        {selectedChecks.map(c => (
          <div key={c.id} className="flex justify-between py-1 border-b border-slate-200 last:border-0">
            <span>{c.invoice_number} — {c.client_name} — {c.reference}</span>
            <span className="font-semibold">{fmt(c.amount)}</span>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
        <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-rem-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Créer bordereau
        </Button>
      </div>
    </Modal>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
 * DÉCLARATIONS ESPÈCES TAB (Sprint 2)
 * ═════════════════════════════════════════════════════════════════════════ */
function EspecesTab({ headers, user }) {
  const [decls, setDecls] = useState([]);
  const [balance, setBalance] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const canWrite = ["superadmin", "directeur_financier", "agent_recouvrement"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [d, b] = await Promise.all([
        axios.get(`${API}/internal-finance/cash-declarations`, { headers }),
        axios.get(`${API}/internal-finance/cash-declarations/balance`, { headers }),
      ]);
      setDecls(d.data);
      setBalance(b.data);
    } catch (e) { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markBanked = async (id) => {
    const bank = window.prompt("Nom de la banque :", "NSIA");
    if (!bank) return;
    const date = window.prompt("Date du dépôt (YYYY-MM-DD):", dateISO());
    if (!date) return;
    try {
      const fd = new FormData();
      fd.append("bank", bank);
      fd.append("banked_date", date);
      await axios.post(`${API}/internal-finance/cash-declarations/${id}/mark-banked`, fd, { headers });
      toast.success("Espèces déposées");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-4" data-testid="especes-tab">
      {balance && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
            <div className="text-[10px] uppercase font-bold text-emerald-700 mb-1">Total déclaré</div>
            <div className="text-xl font-black text-emerald-800">{fmt(balance.total_declared)}</div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <div className="text-[10px] uppercase font-bold text-blue-700 mb-1">Total déposé en banque</div>
            <div className="text-xl font-black text-blue-800">{fmt(balance.total_banked)}</div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <div className="text-[10px] uppercase font-bold text-amber-700 mb-1">💰 Espèces en caisse</div>
            <div className="text-xl font-black text-amber-800">{fmt(balance.current_cash)}</div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-800">Journal de caisse ({decls.length})</h3>
        {canWrite && (
          <Button size="sm" onClick={() => setShowForm(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="new-cash-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle déclaration
          </Button>
        )}
      </div>
      {loading && <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}
      {!loading && decls.length === 0 && <div className="text-center py-16 text-slate-400 border-2 border-dashed rounded-xl">Aucune déclaration</div>}
      {!loading && decls.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Source</th>
                <th className="text-right p-2">Montant</th>
                <th className="text-left p-2">Saisi par</th>
                <th className="text-center p-2">Photo</th>
                <th className="text-center p-2">Statut</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {decls.map(d => (
                <tr key={d.id} className="hover:bg-slate-50" data-testid={`cash-row-${d.id}`}>
                  <td className="p-2 text-xs">{d.declaration_date}</td>
                  <td className="p-2 font-medium">{d.source}</td>
                  <td className="p-2 text-right font-semibold text-emerald-700">{fmt(d.amount)}</td>
                  <td className="p-2 text-xs text-slate-600">{d.created_by_name}</td>
                  <td className="p-2 text-center">
                    {d.photo ? <a href={d.photo} target="_blank" rel="noreferrer" className="text-cyan-600 underline text-xs">📷</a> : "—"}
                  </td>
                  <td className="p-2 text-center">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${d.status === "banked" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`}>
                      {d.status === "banked" ? "✓ Déposé" : "En caisse"}
                    </span>
                  </td>
                  <td className="p-2 text-right">
                    {d.status !== "banked" && canWrite && (
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => markBanked(d.id)} data-testid={`bank-cash-${d.id}`}>
                        Marquer déposé
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <CashDeclarationForm headers={headers} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
}

function CashDeclarationForm({ headers, onClose, onSaved }) {
  const [f, setF] = useState({ amount: 0, source: "", declaration_date: dateISO(), comment: "" });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const camRef = useRef(null);

  const onPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (f.amount <= 0) return toast.error("Montant invalide");
    if (!f.source.trim()) return toast.error("Source obligatoire (ex: vente comptoir)");
    if (!photo) return toast.error("Photo justificatif OBLIGATOIRE");
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      fd.append("photo", photo);
      await axios.post(`${API}/internal-finance/cash-declarations`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Déclaration enregistrée");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Nouvelle déclaration espèces" onClose={onClose}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Montant (FCFA) *</label>
          <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: Number(e.target.value) })} data-testid="cash-amount" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Date *</label>
          <Input type="date" value={f.declaration_date} onChange={(e) => setF({ ...f, declaration_date: e.target.value })} data-testid="cash-date" />
        </div>
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-500 block mb-1">Source / Origine *</label>
        <Input value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}
          placeholder="Ex: Vente comptoir, Encaissement client X, Régularisation…" data-testid="cash-source" />
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-500 block mb-1">Commentaire</label>
        <textarea value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} rows={2} className="w-full border rounded-md p-2 text-sm" data-testid="cash-comment" />
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-red-500 block mb-1">Photo justificatif OBLIGATOIRE *</label>
        <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" data-testid="cash-photo-input" />
        <Button variant="outline" onClick={() => camRef.current?.click()} className="gap-2" data-testid="cash-photo-btn">
          <Camera className="w-4 h-4" /> {photo ? "Changer" : "Prendre / Choisir"}
        </Button>
        {preview && <img src={preview} alt="preview" className="mt-2 max-h-40 rounded border" />}
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
        <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-cash-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Déclarer
        </Button>
      </div>
    </Modal>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
 * DÉPENSES TAB (Sprint 3)
 * ═════════════════════════════════════════════════════════════════════════ */
function DepensesTab({ headers, user }) {
  const [expenses, setExpenses] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [catFilter, setCatFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const canWrite = ["superadmin", "directeur_financier", "agent_comptable"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [e, u] = await Promise.all([
        axios.get(`${API}/internal-finance/expenses`, { headers, params: { category: catFilter || undefined, status: statusFilter || undefined } }),
        axios.get(`${API}/internal-finance/expenses/upcoming`, { headers, params: { days: 30 } }),
      ]);
      setExpenses(e.data);
      setUpcoming(u.data);
    } catch (er) { toast.error("Erreur chargement dépenses"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [catFilter, statusFilter]);

  const markPaid = async (id) => {
    const date = window.prompt("Date de paiement effective (YYYY-MM-DD):", dateISO());
    if (!date) return;
    try {
      const fd = new FormData(); fd.append("payment_date", date);
      await axios.post(`${API}/internal-finance/expenses/${id}/mark-paid`, fd, { headers });
      toast.success("Dépense marquée payée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cette dépense ?")) return;
    try {
      await axios.delete(`${API}/internal-finance/expenses/${id}`, { headers });
      toast.success("Supprimée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const totalPaid = expenses.filter(e => e.status === "paid").reduce((s, e) => s + e.amount, 0);
  const totalPending = expenses.filter(e => e.status !== "paid").reduce((s, e) => s + e.amount, 0);

  return (
    <div className="space-y-4" data-testid="depenses-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
          <div className="text-[10px] uppercase font-bold text-emerald-700 mb-1">Total payé</div>
          <div className="text-xl font-black text-emerald-800">{fmt(totalPaid)}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
          <div className="text-[10px] uppercase font-bold text-amber-700 mb-1">À payer</div>
          <div className="text-xl font-black text-amber-800">{fmt(totalPending)}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <div className="text-[10px] uppercase font-bold text-red-700 mb-1">Échéances 30 j</div>
          <div className="text-xl font-black text-red-800">{upcoming.length} facture{upcoming.length > 1 ? "s" : ""}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <select value={catFilter} onChange={(e) => setCatFilter(e.target.value)} className="h-9 border rounded-md px-2 text-sm" data-testid="exp-cat-filter">
          <option value="">Toutes catégories</option>
          {EXP_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 border rounded-md px-2 text-sm" data-testid="exp-status-filter">
          <option value="">Tous statuts</option>
          <option value="pending">En attente</option>
          <option value="paid">Payée</option>
          <option value="overdue">En retard</option>
        </select>
        <div className="flex-1" />
        {canWrite && (
          <Button size="sm" onClick={() => setShowForm(true)} className="bg-red-600 hover:bg-red-700" data-testid="new-expense-btn">
            <Plus className="w-4 h-4 mr-1" /> Nouvelle dépense
          </Button>
        )}
      </div>

      {loading && <div className="text-center py-6"><Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" /></div>}
      {!loading && expenses.length === 0 && <div className="text-center py-16 text-slate-400 border-2 border-dashed rounded-xl">Aucune dépense</div>}
      {!loading && expenses.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="text-left p-2">Catégorie</th>
                <th className="text-left p-2">Bénéficiaire</th>
                <th className="text-right p-2">Montant</th>
                <th className="text-center p-2">Échéance</th>
                <th className="text-center p-2">Récurrence</th>
                <th className="text-center p-2">Statut</th>
                <th className="text-center p-2">Photo</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expenses.map(e => {
                const cat = EXP_CATEGORIES.find(c => c.id === e.category) || EXP_CATEGORIES[EXP_CATEGORIES.length - 1];
                return (
                  <tr key={e.id} className="hover:bg-slate-50" data-testid={`expense-row-${e.id}`}>
                    <td className="p-2"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${cat.color}`}>{cat.label}</span></td>
                    <td className="p-2 font-medium">{e.beneficiary}</td>
                    <td className="p-2 text-right font-semibold text-red-700">{fmt(e.amount)}</td>
                    <td className="p-2 text-center text-xs">{e.due_date}</td>
                    <td className="p-2 text-center text-xs text-slate-500">{e.is_recurring ? (e.recurrence || "—") : "—"}</td>
                    <td className="p-2 text-center">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded ${e.status === "paid" ? "bg-emerald-100 text-emerald-700" : e.status === "overdue" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                        {e.status === "paid" ? "✓ Payée" : e.status === "overdue" ? "⚠ Retard" : "En attente"}
                      </span>
                    </td>
                    <td className="p-2 text-center">{e.photo ? <a href={e.photo} target="_blank" rel="noreferrer" className="text-cyan-600 text-xs">📷</a> : "—"}</td>
                    <td className="p-2 text-right space-x-1">
                      {e.status !== "paid" && canWrite && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => markPaid(e.id)} data-testid={`pay-expense-${e.id}`}>
                          <Check className="w-3 h-3 mr-1" /> Payer
                        </Button>
                      )}
                      {["superadmin","directeur_financier"].includes(user?.role) && (
                        <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600" onClick={() => del(e.id)} data-testid={`del-expense-${e.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && <ExpenseFormModal headers={headers} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function ExpenseFormModal({ headers, onClose, onSaved }) {
  const [f, setF] = useState({
    category: "salaires", beneficiary: "", amount: 0, mode: "transfer",
    due_date: dateISO(), payment_date: "", reference: "", comment: "",
    is_recurring: false, recurrence: "monthly",
  });
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const camRef = useRef(null);
  const onPhoto = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPhoto(file);
    const r = new FileReader(); r.onload = () => setPreview(r.result); r.readAsDataURL(file);
  };
  const submit = async () => {
    if (!f.beneficiary.trim()) return toast.error("Bénéficiaire obligatoire");
    if (f.amount <= 0) return toast.error("Montant invalide");
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(f).forEach(([k, v]) => fd.append(k, String(v)));
      if (photo) fd.append("photo", photo);
      await axios.post(`${API}/internal-finance/expenses/upload`, fd, { headers: { ...headers, "Content-Type": "multipart/form-data" } });
      toast.success("Dépense enregistrée");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Nouvelle dépense" onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Catégorie *</label>
          <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="exp-category">
            {EXP_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Bénéficiaire *</label>
          <Input value={f.beneficiary} onChange={(e) => setF({ ...f, beneficiary: e.target.value })} placeholder="Nom / société / administration" data-testid="exp-beneficiary" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Montant (FCFA) *</label>
          <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: Number(e.target.value) })} data-testid="exp-amount" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Mode</label>
          <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })} className="w-full h-9 border rounded-md px-2 text-sm" data-testid="exp-mode">
            <option value="cash">Espèces</option>
            <option value="check">Chèque</option>
            <option value="transfer">Virement</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Échéance *</label>
          <Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="exp-due-date" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Date paiement (si déjà payée)</label>
          <Input type="date" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} data-testid="exp-payment-date" />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 block mb-1">Référence</label>
          <Input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} placeholder="Facture, ordre virement…" data-testid="exp-reference" />
        </div>
        <div className="flex items-center gap-2 pt-6">
          <input type="checkbox" checked={f.is_recurring} onChange={(e) => setF({ ...f, is_recurring: e.target.checked })} id="recur-chk" data-testid="exp-recurring" />
          <label htmlFor="recur-chk" className="text-sm">Récurrente</label>
          {f.is_recurring && (
            <select value={f.recurrence} onChange={(e) => setF({ ...f, recurrence: e.target.value })} className="h-8 border rounded px-2 text-xs" data-testid="exp-recurrence">
              <option value="monthly">Mensuelle</option>
              <option value="quarterly">Trimestrielle</option>
              <option value="yearly">Annuelle</option>
            </select>
          )}
        </div>
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-500 block mb-1">Commentaire</label>
        <textarea value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} rows={2} className="w-full border rounded-md p-2 text-sm" data-testid="exp-comment" />
      </div>
      <div className="mt-3">
        <label className="text-xs font-semibold text-slate-500 block mb-1">Photo justificatif</label>
        <input ref={camRef} type="file" accept="image/*" capture="environment" onChange={onPhoto} className="hidden" data-testid="exp-photo-input" />
        <Button variant="outline" onClick={() => camRef.current?.click()} className="gap-2" data-testid="exp-photo-btn">
          <Camera className="w-4 h-4" /> {photo ? "Changer" : "Prendre / Choisir"}
        </Button>
        {preview && <img src={preview} alt="preview" className="mt-2 max-h-40 rounded border" />}
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <Button variant="outline" onClick={onClose} disabled={saving}>Annuler</Button>
        <Button onClick={submit} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="save-expense-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer
        </Button>
      </div>
    </Modal>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
 * TRÉSORERIE CONSOLIDÉE TAB (Sprint 3)
 * ═════════════════════════════════════════════════════════════════════════ */
function TresorerieTab({ headers }) {
  const [period, setPeriod] = useState("month"); // month | quarter | year | custom
  const [from, setFrom] = useState(dateISO().slice(0, 8) + "01");
  const [to, setTo] = useState(dateISO());
  const [data, setData] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [topClients, setTopClients] = useState({ top_payers: [], top_debtors: [] });
  const [loading, setLoading] = useState(false);

  const setPresetPeriod = (p) => {
    setPeriod(p);
    const now = new Date();
    if (p === "month") { setFrom(new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)); setTo(dateISO()); }
    if (p === "quarter") { const q = Math.floor(now.getMonth() / 3); setFrom(new Date(now.getFullYear(), q * 3, 1).toISOString().slice(0, 10)); setTo(dateISO()); }
    if (p === "year") { setFrom(now.getFullYear() + "-01-01"); setTo(dateISO()); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [t, m, c] = await Promise.all([
        axios.get(`${API}/internal-finance/treasury`, { headers, params: { from_date: from, to_date: to } }),
        axios.get(`${API}/internal-finance/treasury/monthly`, { headers, params: { months: 12 } }),
        axios.get(`${API}/internal-finance/treasury/top-clients`, { headers }),
      ]);
      setData(t.data); setMonthly(m.data.series); setTopClients(c.data);
    } catch (e) { toast.error("Erreur trésorerie"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [from, to]);

  const maxNet = Math.max(...monthly.map(m => Math.max(m.recettes, m.depenses, 1)), 1);

  return (
    <div className="space-y-4" data-testid="tresorerie-tab">
      {/* Period selector */}
      <div className="flex flex-wrap items-center gap-2 bg-white border rounded-xl p-2">
        <div className="inline-flex bg-slate-100 rounded p-0.5">
          {["month","quarter","year","custom"].map(p => (
            <button key={p} onClick={() => setPresetPeriod(p)} data-testid={`period-${p}`}
              className={`px-3 py-1 rounded text-xs font-semibold ${period === p ? "bg-white shadow" : "text-slate-500"}`}>
              {{ month:"Mois", quarter:"Trimestre", year:"Année", custom:"Personnalisé" }[p]}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-slate-500">Du</span>
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPeriod("custom"); }} className="h-8 w-36" data-testid="period-from" />
          <span className="text-xs text-slate-500">au</span>
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPeriod("custom"); }} className="h-8 w-36" data-testid="period-to" />
        </div>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
      </div>

      {loading && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>}

      {!loading && data && (
        <>
          {/* 4 Big KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white rounded-xl p-4">
              <div className="text-[10px] uppercase font-bold opacity-80 mb-1">Recettes période</div>
              <div className="text-2xl font-black" data-testid="kpi-recettes">{fmt(data.recettes)}</div>
            </div>
            <div className="bg-gradient-to-br from-red-500 to-rose-500 text-white rounded-xl p-4">
              <div className="text-[10px] uppercase font-bold opacity-80 mb-1">Dépenses période</div>
              <div className="text-2xl font-black" data-testid="kpi-depenses">{fmt(data.depenses)}</div>
            </div>
            <div className={`bg-gradient-to-br ${data.gains_nets >= 0 ? "from-blue-500 to-indigo-500" : "from-orange-500 to-red-600"} text-white rounded-xl p-4`}>
              <div className="text-[10px] uppercase font-bold opacity-80 mb-1">💰 Gains nets</div>
              <div className="text-2xl font-black" data-testid="kpi-gains">{fmt(data.gains_nets)}</div>
            </div>
            <div className="bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-xl p-4">
              <div className="text-[10px] uppercase font-bold opacity-80 mb-1">Créances à recouvrer</div>
              <div className="text-2xl font-black" data-testid="kpi-outstanding">{fmt(data.outstanding_receivables)}</div>
            </div>
          </div>

          {/* Monthly bar chart */}
          <div className="bg-white border rounded-xl p-4">
            <h3 className="font-bold text-slate-800 mb-3">Évolution mensuelle — 12 derniers mois</h3>
            <div className="flex items-end gap-1 h-48">
              {monthly.map((m, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <div className="flex items-end gap-0.5 h-40 w-full justify-center">
                    <div className="w-3 bg-emerald-500 rounded-t" style={{ height: `${(m.recettes / maxNet) * 100}%` }} title={`Recettes: ${fmt(m.recettes)}`}></div>
                    <div className="w-3 bg-red-500 rounded-t" style={{ height: `${(m.depenses / maxNet) * 100}%` }} title={`Dépenses: ${fmt(m.depenses)}`}></div>
                  </div>
                  <div className="text-[9px] text-slate-500 truncate w-full text-center">{m.label}</div>
                  <div className={`text-[10px] font-bold ${m.net >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {m.net >= 0 ? "+" : ""}{Math.round(m.net / 1000)}k
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-4 mt-3 text-xs">
              <span className="flex items-center gap-1"><div className="w-3 h-3 bg-emerald-500 rounded"></div>Recettes</span>
              <span className="flex items-center gap-1"><div className="w-3 h-3 bg-red-500 rounded"></div>Dépenses</span>
            </div>
          </div>

          {/* Répartition dépenses + top clients */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border rounded-xl p-4">
              <h3 className="font-bold text-slate-800 mb-3">Répartition des dépenses</h3>
              {Object.keys(data.depenses_by_category).length === 0 && <div className="text-sm text-slate-400 text-center py-8">Aucune dépense sur la période</div>}
              {Object.entries(data.depenses_by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => {
                const c = EXP_CATEGORIES.find(x => x.id === cat) || { label: cat, color: "bg-slate-100 text-slate-500" };
                const pct = data.depenses ? (amt / data.depenses) * 100 : 0;
                return (
                  <div key={cat} className="mb-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className={`px-2 py-0.5 rounded ${c.color}`}>{c.label}</span>
                      <span className="font-semibold">{fmt(amt)} ({pct.toFixed(1)}%)</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-red-500" style={{ width: `${pct}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="bg-white border rounded-xl p-4">
              <h3 className="font-bold text-slate-800 mb-3">Top clients — Bons vs Mauvais payeurs</h3>
              <div className="mb-3">
                <div className="text-[10px] uppercase font-bold text-emerald-700 mb-1">Top payeurs</div>
                {topClients.top_payers.slice(0, 5).map(c => (
                  <div key={c.id} className="flex justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                    <span>{c.name}</span>
                    <span className="font-semibold text-emerald-700">{fmt(c.total_paid)}</span>
                  </div>
                ))}
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-red-700 mb-1">Créanciers</div>
                {topClients.top_debtors.slice(0, 5).map(c => (
                  <div key={c.id} className="flex justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                    <span>{c.name}</span>
                    <span className="font-semibold text-red-700">{fmt(c.balance_due)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recettes détaillées par mode */}
          <div className="bg-white border rounded-xl p-4">
            <h3 className="font-bold text-slate-800 mb-3">Recettes par mode de paiement</h3>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(data.recettes_by_mode).map(([m, v]) => (
                <div key={m} className="bg-slate-50 rounded-lg p-3 text-center">
                  <ModeIcon mode={m} />
                  <div className="text-xl font-black mt-1">{fmt(v)}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
 * ALERTES INTELLIGENTES TAB (Sprint 4)
 * ═════════════════════════════════════════════════════════════════════════ */
function AlertesTab({ headers }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/internal-finance/alerts/scan`, { headers });
      setAlerts(res.data.alerts || []);
    } catch (e) { toast.error("Erreur chargement alertes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const cfg = {
    critical: { bg: "bg-red-50 border-red-300", txt: "text-red-800", icon: "🚨", label: "CRITIQUE" },
    high: { bg: "bg-orange-50 border-orange-300", txt: "text-orange-800", icon: "⚠️", label: "HAUTE" },
    medium: { bg: "bg-amber-50 border-amber-300", txt: "text-amber-800", icon: "🔔", label: "MOYENNE" },
    low: { bg: "bg-blue-50 border-blue-300", txt: "text-blue-800", icon: "ℹ️", label: "INFO" },
  };
  const byType = {
    invoice_overdue: { title: "Factures en retard", icon: FileText, color: "text-red-600" },
    check_undeposited: { title: "Chèques non déposés", icon: Banknote, color: "text-blue-600" },
    expense_upcoming: { title: "Échéances à venir", icon: ReceiptText, color: "text-orange-600" },
    cash_high: { title: "Trésorerie caisse", icon: Wallet, color: "text-amber-600" },
    heavy_debtor: { title: "Créanciers importants", icon: Users, color: "text-violet-600" },
  };
  const grouped = alerts.reduce((acc, a) => { (acc[a.type] = acc[a.type] || []).push(a); return acc; }, {});
  const counts = { critical: 0, high: 0, medium: 0, low: 0 };
  alerts.forEach(a => counts[a.severity] = (counts[a.severity] || 0) + 1);

  return (
    <div className="space-y-4" data-testid="alertes-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-slate-800">Alertes intelligentes ({alerts.length})</h3>
        <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Rescanner</Button>
      </div>

      {/* Severity summary */}
      <div className="grid grid-cols-4 gap-2">
        {["critical","high","medium","low"].map(s => (
          <div key={s} className={`rounded-lg p-3 border ${cfg[s].bg}`}>
            <div className="text-lg">{cfg[s].icon}</div>
            <div className={`text-xs font-bold uppercase ${cfg[s].txt}`}>{cfg[s].label}</div>
            <div className="text-2xl font-black">{counts[s]}</div>
          </div>
        ))}
      </div>

      {loading && <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>}
      {!loading && alerts.length === 0 && (
        <div className="text-center py-16 bg-emerald-50 border-2 border-emerald-200 border-dashed rounded-xl">
          <div className="text-4xl mb-2">✅</div>
          <div className="font-bold text-emerald-800">Aucune alerte active</div>
          <div className="text-sm text-emerald-600 mt-1">Votre gestion est à jour, tout va bien !</div>
        </div>
      )}

      {!loading && Object.entries(grouped).map(([type, list]) => {
        const meta = byType[type] || { title: type, icon: AlertCircle, color: "text-slate-500" };
        const Icon = meta.icon;
        return (
          <div key={type} className="bg-white border rounded-xl overflow-hidden">
            <div className="px-4 py-2 bg-slate-50 border-b flex items-center gap-2">
              <Icon className={`w-4 h-4 ${meta.color}`} />
              <span className="font-semibold text-slate-800">{meta.title}</span>
              <span className="text-xs text-slate-500">({list.length})</span>
            </div>
            <div className="divide-y">
              {list.map(a => (
                <div key={a.id} className={`p-3 border-l-4 ${cfg[a.severity].bg}`} data-testid={`alert-${a.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-lg">{cfg[a.severity].icon}</span>
                        <span className="font-semibold text-slate-800">{a.title}</span>
                        <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${cfg[a.severity].txt}`}>{cfg[a.severity].label}</span>
                      </div>
                      <div className="text-sm text-slate-600">{a.description}</div>
                    </div>
                    {a.amount ? (<div className="text-right"><div className="text-lg font-black text-slate-800">{fmt(a.amount)}</div>{a.days !== undefined && <div className="text-[10px] text-slate-500">{a.days} j</div>}</div>) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ═════════════════════════════════════════════════════════════════════════
 * SENTINELLE IA TAB (Sprint 4) — Chat + Rapports narratifs
 * ═════════════════════════════════════════════════════════════════════════ */
function SentinelleTab({ headers }) {
  const [subTab, setSubTab] = useState("chat"); // chat | reports
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [reports, setReports] = useState([]);
  const [generating, setGenerating] = useState(false);
  const chatEndRef = useRef(null);

  useEffect(() => { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  useEffect(() => { loadReports(); }, []);

  const loadReports = async () => {
    try {
      const r = await axios.get(`${API}/internal-finance/sentinel/reports`, { headers });
      setReports(r.data);
    } catch {}
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    const q = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: q }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/internal-finance/sentinel/chat`,
        { message: q, session_id: sessionId }, { headers, timeout: 60000 });
      setSessionId(res.data.session_id);
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sentinelle indisponible");
      setMessages(prev => [...prev, { role: "assistant", content: "⚠️ Désolé, je n'ai pas pu traiter votre demande. Réessayez dans un instant." }]);
    } finally { setSending(false); }
  };

  const generateReport = async (period) => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/internal-finance/sentinel/report`,
        null, { headers, params: { period }, timeout: 90000 });
      toast.success(`Rapport ${period === "weekly" ? "hebdomadaire" : "mensuel"} généré`);
      setReports([res.data, ...reports]);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setGenerating(false); }
  };

  const suggestions = [
    "Résume ma trésorerie du mois",
    "Quels clients sont en retard ?",
    "Combien dois-je à payer cette semaine ?",
    "Comment améliorer mon recouvrement ?",
  ];

  return (
    <div className="space-y-4" data-testid="sentinelle-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-violet-600 flex items-center justify-center">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-slate-800">Sentinelle CMES</h3>
            <p className="text-xs text-slate-500">Agent IA propulsé par Claude Sonnet 4.5 — accès sécurisé à vos données internes</p>
          </div>
        </div>
        <div className="inline-flex bg-slate-100 rounded p-0.5">
          <button onClick={() => setSubTab("chat")} data-testid="subtab-chat"
            className={`px-3 py-1 rounded text-xs font-semibold ${subTab === "chat" ? "bg-white shadow" : "text-slate-500"}`}>💬 Chat</button>
          <button onClick={() => setSubTab("reports")} data-testid="subtab-reports"
            className={`px-3 py-1 rounded text-xs font-semibold ${subTab === "reports" ? "bg-white shadow" : "text-slate-500"}`}>📄 Rapports</button>
        </div>
      </div>

      {subTab === "chat" && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="h-[420px] overflow-y-auto p-4 space-y-3 bg-gradient-to-b from-slate-50 to-white" data-testid="chat-window">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <Bot className="w-12 h-12 text-fuchsia-400 mx-auto mb-3" />
                <div className="font-semibold text-slate-700 mb-1">Bonjour Dr KOBOU 👋</div>
                <div className="text-sm text-slate-500 mb-4">Je suis Sentinelle, votre agent IA. Posez-moi vos questions sur la gestion interne.</div>
                <div className="flex flex-wrap gap-2 justify-center max-w-lg mx-auto">
                  {suggestions.map(s => (
                    <button key={s} onClick={() => setInput(s)}
                      className="text-xs bg-white border border-fuchsia-200 rounded-full px-3 py-1 hover:bg-fuchsia-50 text-fuchsia-700" data-testid={`suggest-${s.slice(0,10)}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${m.role === "user"
                  ? "bg-gradient-to-br from-cyan-500 to-blue-600 text-white"
                  : "bg-white border border-slate-200 text-slate-800"}`}>
                  <div className="text-sm whitespace-pre-wrap">{m.content}</div>
                </div>
              </div>
            ))}
            {sending && <div className="flex justify-start"><div className="bg-white border rounded-2xl px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-fuchsia-500" /></div></div>}
            <div ref={chatEndRef}></div>
          </div>
          <div className="border-t p-3 flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
              placeholder="Posez votre question…" disabled={sending} data-testid="chat-input" className="flex-1" />
            <Button onClick={send} disabled={sending || !input.trim()} className="bg-gradient-to-r from-fuchsia-500 to-violet-600" data-testid="chat-send">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      )}

      {subTab === "reports" && (
        <div className="space-y-3">
          <div className="bg-gradient-to-r from-fuchsia-500 to-violet-600 rounded-xl p-4 text-white flex flex-wrap items-center gap-3">
            <div className="flex-1">
              <div className="font-bold">Générer un rapport exécutif</div>
              <div className="text-xs opacity-80">Analyse narrative IA de votre gestion sur la période</div>
            </div>
            <Button onClick={() => generateReport("weekly")} disabled={generating} className="bg-white text-fuchsia-700 hover:bg-fuchsia-50" data-testid="gen-weekly">
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />} Hebdomadaire
            </Button>
            <Button onClick={() => generateReport("monthly")} disabled={generating} className="bg-white text-fuchsia-700 hover:bg-fuchsia-50" data-testid="gen-monthly">
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />} Mensuel
            </Button>
          </div>
          {reports.length === 0 && <div className="text-center py-16 text-slate-400 border-2 border-dashed rounded-xl">Aucun rapport encore généré</div>}
          {reports.map(r => (
            <div key={r.id} className="bg-white border rounded-xl p-4" data-testid={`report-${r.id}`}>
              <div className="flex items-center justify-between mb-3 pb-3 border-b">
                <div>
                  <div className="text-xs uppercase font-bold text-fuchsia-600">Rapport {r.period === "weekly" ? "hebdomadaire" : "mensuel"}</div>
                  <div className="text-xs text-slate-500 mt-1">Généré le {new Date(r.generated_at).toLocaleString("fr-FR")}</div>
                </div>
                <div className="text-xs bg-fuchsia-50 border border-fuchsia-200 rounded-full px-3 py-1 text-fuchsia-700">
                  {r.alerts_count} alerte{r.alerts_count > 1 ? "s" : ""}
                </div>
              </div>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap text-sm text-slate-700">
                {r.content_md}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



function AuditTab({ headers }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [entityFilter, setEntityFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/internal-finance/audit-log`, { headers, params: { entity_type: entityFilter || undefined } });
      setLogs(res.data);
    } catch (e) { toast.error("Erreur chargement audit"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [entityFilter]);

  const actionLabel = (a) => ({ create: "Création", update: "Modification", delete: "Suppression", cancel: "Annulation", validate: "Validation", import: "Import", credit: "Crédit", reject: "Rejet", bank: "Dépôt banque", pay: "Paiement" }[a] || a);
  const entityLabel = (e) => ({ internal_client: "Client", internal_invoice: "Facture", internal_payment: "Paiement", internal_remittance: "Remise", internal_cash_declaration: "Espèces", internal_expense: "Dépense" }[e] || e);

  return (
    <div className="space-y-3" data-testid="audit-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-slate-400" />
        <select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} className="h-9 border rounded-md px-2 text-sm" data-testid="audit-filter">
          <option value="">Toutes les entités</option>
          <option value="internal_client">Clients</option>
          <option value="internal_invoice">Factures</option>
          <option value="internal_payment">Paiements</option>
          <option value="internal_remittance">Remises chèques</option>
          <option value="internal_cash_declaration">Espèces</option>
        </select>
        <Button size="sm" variant="outline" onClick={load}><RefreshCw className="w-4 h-4" /></Button>
        <span className="text-xs text-slate-400 ml-auto">{logs.length} entrées</span>
      </div>
      {loading && <div className="text-center py-8 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
      {!loading && logs.length === 0 && <div className="text-center py-16 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">Aucun événement enregistré</div>}
      {!loading && logs.length > 0 && (
        <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Date / Heure</th>
                <th className="text-left px-3 py-2">Utilisateur</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Entité</th>
                <th className="text-left px-3 py-2">ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {logs.map(l => (
                <tr key={l.id} className="hover:bg-slate-50" data-testid={`audit-row-${l.id}`}>
                  <td className="px-3 py-2 text-xs text-slate-500 font-mono">{new Date(l.at).toLocaleString("fr-FR")}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-800">{l.actor_email}</div>
                    <div className="text-[10px] text-slate-500 uppercase">{l.actor_role}</div>
                  </td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 text-[10px] rounded bg-slate-100 text-slate-700 uppercase font-semibold">{actionLabel(l.action)}</span></td>
                  <td className="px-3 py-2 text-slate-600">{entityLabel(l.entity_type)}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-400 truncate max-w-xs">{l.entity_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
